import { useMemo } from 'react';
import styled from 'styled-components';

import { Delete16 } from 'ui/icons/aurora/16px/Delete16';
import { FolderMove16 } from 'ui/icons/aurora/16px/FolderMove16';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { spacing } from 'ui/theme';
import { AssetListPending } from './TakeoverAssetList';
import { useTakeoverContext } from '../../../contexts/Takeover';
import { useUploadAssetsContext } from '../../../contexts/UploadAssetsContext';
import { useTranslations } from '../../../hooks/useTranslations';
import { TakeoverIntent } from '../../../types';
import { useCreativeLibraryFolderModals } from 'modules/creativeLibrary/hooks/useCreativeLibraryModals';
import { useDeleteImageOverlayContext } from 'modules/creativeLibrary/contexts/DeleteImageOverlayContext';

const ButtonBar = styled.div<{ $singleItem?: boolean }>`
  display: flex;
  flex: 0 0 auto;
  justify-content: ${({ $singleItem }) => ($singleItem ? 'flex-end' : 'space-between')};
  padding: ${spacing.base};
  section {
    column-gap: ${spacing.base};
    display: flex;
  }
`;

const ScrollableContainer = styled.div`
  flex: 1 1 auto;
  overflow: auto;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

export const testIds = {
  buttonDelete: 'cl-index-takeover-list-button-delete',
  buttonDeselectAll: 'cl-index-takeover-list-button-deselect',
  buttonSelectAll: 'cl-index-takeover-list-button-select-all',
  buttonMoveToFolder: 'cl-index-takeover-list-button-move-to-folder',
};

export const TakeoverList = () => {
  const { t } = useTranslations();
  const modals = useCreativeLibraryFolderModals();
  const { pendingImages, setSelectedAssets } = useUploadAssetsContext();
  const { setImgsIdsToDelete } = useDeleteImageOverlayContext();
  const { currentIntent } = useTakeoverContext();
  const idsSelected = useMemo(
    () => pendingImages.filter((pi) => pi.isSelected).map((pi) => pi.id),
    [pendingImages],
  );
  const isAllSelected = useMemo(() => pendingImages.every((pi) => pi.isSelected), [pendingImages]);

  const handleDelete = () => {
    setImgsIdsToDelete(idsSelected);
  };

  const handleMoveToFolder = () => {
    modals.move.toggle(true);
  };

  return (
    <>
      <ButtonBar $singleItem={pendingImages.length === 1}>
        {pendingImages.length > 1 && (
          <section>
            {isAllSelected ? (
              <Button
                data-test={testIds.buttonDeselectAll}
                onClick={() => setSelectedAssets([])}
                size={BUTTON_SIZES.SMALL}
                variant={BUTTON_VARIANTS.SECONDARY}
              >
                {t('button.deselectAll')}
              </Button>
            ) : (
              <Button
                data-test={testIds.buttonSelectAll}
                onClick={() => setSelectedAssets(pendingImages.map((pi) => pi.id))}
                size={BUTTON_SIZES.SMALL}
                variant={BUTTON_VARIANTS.SECONDARY}
              >
                {t('button.selectAll')}
              </Button>
            )}
          </section>
        )}
        <section>
          <Button
            data-test={testIds.buttonDelete}
            isDisabled={!idsSelected.length}
            onClick={handleDelete}
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.SECONDARYDESTRUCTIVE}
          >
            <Delete16 />
            {t('button.delete')}
          </Button>
          <Button
            data-test={testIds.buttonMoveToFolder}
            isDisabled={!idsSelected.length}
            onClick={handleMoveToFolder}
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.SECONDARY}
          >
            <FolderMove16 />
            {currentIntent === TakeoverIntent.UPLOAD
              ? t('button.moveToAnotherFolder')
              : t('button.moveToFolder')}
          </Button>
        </section>
      </ButtonBar>
      <ScrollableContainer>
        <List>
          <AssetListPending />
        </List>
      </ScrollableContainer>
    </>
  );
};
